import { usePageTitle } from '../hooks/usePageTitle';
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useTheme } from '../contexts/ThemeContext';

interface Ogrenci {
  id: string;
  ad: string;
  soyad: string;
  email: string;
  tel?: string;
  sube_id?: string;
  apple_id?: string;
  google_id?: string;
  mobil_app: boolean;
  dersler: { ders_id: string; ders_adi: string }[];
}

export default function TumKullanicilar() {
  usePageTitle('Tüm Kullanıcılar');
  const { isDark } = useTheme();
  const card      = isDark ? '#1a1a1a' : 'white';
  const cardAlt   = isDark ? '#1e1e1e' : '#f8f8f8';
  const border    = isDark ? '#2a2a2a' : '#eee';
  const borderSub = isDark ? '#202020' : '#f5f5f5';
  const textSec   = isDark ? '#999'    : '#888';
  const textMuted = isDark ? '#666'    : '#aaa';
  const inputBg   = isDark ? '#111'    : 'white';
  const inputBorder = isDark ? '#333'  : '#ddd';

  const [liste, setListe]           = useState<Ogrenci[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [arama, setArama]           = useState('');
  const [filtre, setFiltre]         = useState<'tum' | 'mobil' | 'mobil_yok'>('tum');
  const [subeler, setSubeler]       = useState<{ id: string; ad: string }[]>([]);
  const [subeFiltre, setSubeFiltre] = useState('');

  // Birleştirme modal
  const [kaynakOgrenci, setKaynakOgrenci] = useState<Ogrenci | null>(null);
  const [hedefArama, setHedefArama]       = useState('');
  const [hedefListe, setHedefListe]       = useState<Ogrenci[]>([]);
  const [hedefSecilen, setHedefSecilen]   = useState<Ogrenci | null>(null);
  const [aramaYukleniyor, setAramaYukleniyor] = useState(false);
  const [islemYapiliyor, setIslemYapiliyor]   = useState(false);
  const [hata, setHata]                       = useState('');

  const yukle = useCallback(() => {
    setYukleniyor(true);
    api.get('/api/ogrenciler?tum=true')
      .then(r => setListe(r.data))
      .catch(() => {})
      .finally(() => setYukleniyor(false));
  }, []);

  useEffect(() => { yukle(); }, [yukle]);

  useEffect(() => {
    api.get('/api/subeler/public').then(r => setSubeler(r.data)).catch(() => {});
  }, []);

  const hedefAra = useCallback((q: string) => {
    if (!q.trim()) { setHedefListe([]); return; }
    setAramaYukleniyor(true);
    api.get(`/api/ogrenciler?arama=${encodeURIComponent(q)}&tum=true`)
      .then(r => setHedefListe(r.data))
      .catch(() => {})
      .finally(() => setAramaYukleniyor(false));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => hedefAra(hedefArama), 300);
    return () => clearTimeout(t);
  }, [hedefArama, hedefAra]);

  const modalKapat = () => {
    setKaynakOgrenci(null);
    setHedefArama('');
    setHedefListe([]);
    setHedefSecilen(null);
    setHata('');
  };

  const birlestir = async () => {
    if (!kaynakOgrenci || !hedefSecilen) return;
    // kaynakOgrenci = tıklanan (KALACAK), hedefSecilen = arama ile seçilen (SİLİNECEK)
    if (!window.confirm(
      `"${hedefSecilen.ad} ${hedefSecilen.soyad}" hesabı silinerek tüm verileri\n"${kaynakOgrenci.ad} ${kaynakOgrenci.soyad}" hesabına aktarılacak.\n\nBu işlem geri alınamaz. Emin misiniz?`
    )) return;
    setIslemYapiliyor(true);
    setHata('');
    try {
      // hedefSecilen = kaynak (silinecek), kaynakOgrenci = hedef (kalacak)
      await api.post(`/api/ogrenciler/${hedefSecilen.id}/birlestir`, { hedef_id: kaynakOgrenci.id });
      alert(`Birleştirme tamamlandı. "${hedefSecilen.ad} ${hedefSecilen.soyad}" hesabı silindi.`);
      modalKapat();
      yukle();
    } catch (err: any) {
      setHata(err.response?.data?.hata || 'Birleştirme başarısız');
    } finally {
      setIslemYapiliyor(false);
    }
  };

  const aramaLC = arama.toLowerCase();
  const gorunen = liste.filter(o => {
    if (filtre === 'mobil' && !o.mobil_app) return false;
    if (filtre === 'mobil_yok' && o.mobil_app) return false;
    if (subeFiltre && o.sube_id !== subeFiltre) return false;
    if (aramaLC) {
      const tam = `${o.ad} ${o.soyad} ${o.email} ${o.tel || ''}`.toLowerCase();
      if (!tam.includes(aramaLC)) return false;
    }
    return true;
  });

  const mobilSayisi = liste.filter(o => o.mobil_app).length;

  return (
    <div style={{ padding: '24px 24px 48px' }}>
      {/* Başlık */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Tüm Kullanıcılar</h1>
        <div style={{ fontSize: 13, color: textMuted, marginTop: 4 }}>
          Tüm şubelerden öğrenciler — birleştirme ve mobil uygulama takibi
        </div>
      </div>

      {/* İstatistik */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Toplam', value: liste.length, renk: '#534AB7', bg: isDark ? 'rgba(83,74,183,0.15)' : '#EEEDFE' },
          { label: 'Mobil Uygulama', value: mobilSayisi, renk: '#1D9E75', bg: isDark ? 'rgba(29,158,117,0.15)' : '#E1F5EE' },
          { label: 'Uygulama Yok', value: liste.length - mobilSayisi, renk: '#F59E0B', bg: isDark ? 'rgba(245,158,11,0.15)' : '#FEF3C7' },
        ].map(k => (
          <div key={k.label} style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, padding: '12px 18px', minWidth: 120 }}>
            <div style={{ fontSize: 11, color: textMuted, marginBottom: 4 }}>{k.label.toUpperCase()}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: k.renk }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Filtreler */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          placeholder="Ad, soyad, e-posta, telefon..."
          value={arama}
          onChange={e => setArama(e.target.value)}
          style={{ flex: 1, minWidth: 200, padding: '9px 12px', borderRadius: 8, border: `1px solid ${inputBorder}`, background: inputBg, color: isDark ? '#fff' : '#000', fontSize: 13 }}
        />
        <select
          value={subeFiltre}
          onChange={e => setSubeFiltre(e.target.value)}
          style={{ padding: '9px 12px', borderRadius: 8, border: `1px solid ${inputBorder}`, background: inputBg, color: isDark ? '#fff' : '#000', fontSize: 13 }}
        >
          <option value="">Tüm şubeler</option>
          {subeler.map(s => <option key={s.id} value={s.id}>{s.ad}</option>)}
        </select>
        <div style={{ display: 'flex', border: `1px solid ${border}`, borderRadius: 8, overflow: 'hidden' }}>
          {([
            { key: 'tum', label: 'Tümü' },
            { key: 'mobil', label: '📱 Kurulu' },
            { key: 'mobil_yok', label: 'Kurulmamış' },
          ] as const).map(f => (
            <button
              key={f.key}
              onClick={() => setFiltre(f.key)}
              style={{
                padding: '8px 14px', border: 'none', fontSize: 12, cursor: 'pointer',
                background: filtre === f.key ? '#534AB7' : cardAlt,
                color: filtre === f.key ? 'white' : textSec,
                fontWeight: filtre === f.key ? 600 : 400,
              }}
            >{f.label}</button>
          ))}
        </div>
      </div>

      {/* Tablo */}
      <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, overflow: 'hidden' }}>
        {yukleniyor ? (
          <div style={{ padding: 40, textAlign: 'center', color: textMuted }}>Yükleniyor...</div>
        ) : gorunen.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: textMuted }}>Kullanıcı bulunamadı.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: cardAlt, borderBottom: `1px solid ${border}` }}>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 600, color: textSec, fontSize: 11 }}>AD SOYAD</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 600, color: textSec, fontSize: 11 }}>E-POSTA / TEL</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 600, color: textSec, fontSize: 11 }}>GİRİŞ</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 600, color: textSec, fontSize: 11 }}>DERSLER</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 600, color: textSec, fontSize: 11 }}>MOBİL</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 600, color: textSec, fontSize: 11 }}>İŞLEM</th>
              </tr>
            </thead>
            <tbody>
              {gorunen.map((o, i) => (
                <tr key={o.id} style={{ borderTop: `1px solid ${borderSub}` }}>
                  <td style={{ padding: '11px 16px' }}>
                    <Link to={`/ogrenciler/${o.id}`} style={{ fontWeight: 600, color: '#534AB7', textDecoration: 'none' }}>
                      {o.ad} {o.soyad}
                    </Link>
                  </td>
                  <td style={{ padding: '11px 16px' }}>
                    <div style={{ color: textSec, fontSize: 12 }}>{o.email}</div>
                    {o.tel && <div style={{ color: textMuted, fontSize: 11, marginTop: 2 }}>{o.tel}</div>}
                  </td>
                  <td style={{ padding: '11px 16px' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {o.apple_id && (
                        <span style={{ background: isDark ? '#222' : '#f0f0f0', color: isDark ? '#ccc' : '#444', borderRadius: 4, padding: '2px 6px', fontSize: 11 }}>
                           Apple
                        </span>
                      )}
                      {o.google_id && (
                        <span style={{ background: isDark ? '#1a2a1a' : '#e8f5e9', color: '#2E7D32', borderRadius: 4, padding: '2px 6px', fontSize: 11 }}>
                          G Google
                        </span>
                      )}
                      {!o.apple_id && !o.google_id && (
                        <span style={{ color: textMuted, fontSize: 11 }}>E-posta</span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '11px 16px', color: textSec, fontSize: 12 }}>
                    {o.dersler?.length > 0
                      ? o.dersler.map(d => d.ders_adi).join(', ')
                      : <span style={{ color: textMuted }}>—</span>}
                  </td>
                  <td style={{ padding: '11px 16px' }}>
                    {o.mobil_app
                      ? <span style={{ background: isDark ? 'rgba(29,158,117,0.2)' : '#E1F5EE', color: '#1D9E75', borderRadius: 20, padding: '3px 8px', fontSize: 11, fontWeight: 600 }}>📱 Kurulu</span>
                      : <span style={{ color: textMuted, fontSize: 11 }}>—</span>}
                  </td>
                  <td style={{ padding: '11px 16px' }}>
                    <button
                      onClick={() => { setKaynakOgrenci(o); setHedefSecilen(null); setHedefArama(''); setHedefListe([]); setHata(''); }}
                      style={{ background: 'transparent', color: textSec, border: `1px solid ${border}`, padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
                    >
                      ⇌ Birleştir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 10, fontSize: 12, color: textMuted, textAlign: 'right' }}>
        {gorunen.length} kayıt gösteriliyor
      </div>

      {/* Birleştirme Modalı */}
      {kaynakOgrenci && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={e => { if (e.target === e.currentTarget) modalKapat(); }}
        >
          <div style={{ background: card, borderRadius: 14, padding: 28, width: 520, maxWidth: '95vw', maxHeight: '85vh', overflow: 'auto' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Hesap Birleştir</div>

            {/* Kalacak hesap (tıklanan = yeni/mobil hesap) */}
            <div style={{ background: isDark ? '#0a1a0a' : '#eafaf1', border: `1px solid ${isDark ? '#1a3a1a' : '#a3e4b8'}`, borderRadius: 10, padding: 14, marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: '#1D9E75', fontWeight: 600, marginBottom: 6 }}>KALACAK HESAP (bu hesap korunacak)</div>
              <div style={{ fontWeight: 600 }}>{kaynakOgrenci.ad} {kaynakOgrenci.soyad}</div>
              <div style={{ fontSize: 12, color: textSec }}>{kaynakOgrenci.email}</div>
              {kaynakOgrenci.dersler?.length > 0 && (
                <div style={{ fontSize: 11, color: textMuted, marginTop: 4 }}>
                  Dersler: {kaynakOgrenci.dersler.map(d => d.ders_adi).join(', ')}
                </div>
              )}
              <div style={{ fontSize: 11, color: textMuted, marginTop: 4 }}>
                {kaynakOgrenci.apple_id ? ' Apple · ' : ''}{kaynakOgrenci.google_id ? ' Google · ' : ''}{kaynakOgrenci.mobil_app ? '📱 Mobil kurulu' : ''}
              </div>
            </div>

            {/* Hedef arama */}
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Silinecek eski hesabı seçin (verileri yukarıdaki hesaba aktarılacak)</div>
            <input
              autoFocus
              placeholder="Ad, soyad veya e-posta ile ara..."
              value={hedefArama}
              onChange={e => setHedefArama(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${inputBorder}`, background: inputBg, color: isDark ? '#fff' : '#000', fontSize: 13, boxSizing: 'border-box', marginBottom: 10 }}
            />

            {aramaYukleniyor && <div style={{ color: textMuted, fontSize: 13, marginBottom: 10 }}>Aranıyor...</div>}

            {hedefListe.length > 0 && (
              <div style={{ border: `1px solid ${border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 14, maxHeight: 200, overflowY: 'auto' }}>
                {hedefListe
                  .filter(o => o.id !== kaynakOgrenci.id)
                  .map((o, i) => (
                    <div
                      key={o.id}
                      onClick={() => setHedefSecilen(o)}
                      style={{
                        padding: '10px 14px', cursor: 'pointer',
                        background: hedefSecilen?.id === o.id ? (isDark ? '#1a1a3a' : '#EEEDFE') : (i % 2 === 0 ? card : cardAlt),
                        borderTop: i > 0 ? `1px solid ${borderSub}` : 'none',
                        borderLeft: hedefSecilen?.id === o.id ? '3px solid #534AB7' : '3px solid transparent',
                      }}
                    >
                      <div style={{ fontWeight: 500, color: hedefSecilen?.id === o.id ? '#534AB7' : 'inherit' }}>
                        {o.ad} {o.soyad}
                        {o.mobil_app && <span style={{ marginLeft: 6, fontSize: 11 }}>📱</span>}
                      </div>
                      <div style={{ fontSize: 11, color: textMuted }}>
                        {o.email}
                        {o.dersler?.length > 0 && ` · ${o.dersler.map(d => d.ders_adi).join(', ')}`}
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {hedefSecilen && (
              <div style={{ background: isDark ? '#1a0a0a' : '#FEF2F2', border: `1px solid ${isDark ? '#3a1a1a' : '#FCA5A5'}`, borderRadius: 8, padding: 12, marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: '#DC2626', fontWeight: 600, marginBottom: 4 }}>SİLİNECEK HESAP</div>
                <div style={{ fontWeight: 600 }}>{hedefSecilen.ad} {hedefSecilen.soyad}</div>
                <div style={{ fontSize: 12, color: textSec }}>{hedefSecilen.email}</div>
                <div style={{ fontSize: 12, color: textMuted, marginTop: 6 }}>
                  Bu hesaptaki tüm krediler, ders kayıtları ve kimlikler kalacak hesaba aktarılacak. Bu hesap silinecek.
                </div>
              </div>
            )}

            {hata && (
              <div style={{ background: '#fef2f2', color: '#dc2626', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>
                {hata}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={modalKapat}
                style={{ padding: '9px 18px', borderRadius: 8, border: `1px solid ${border}`, background: 'transparent', cursor: 'pointer', fontSize: 13, color: isDark ? '#fff' : '#000' }}
              >
                İptal
              </button>
              <button
                onClick={birlestir}
                disabled={!hedefSecilen || islemYapiliyor}
                style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: hedefSecilen ? '#DC2626' : '#999', color: 'white', cursor: hedefSecilen ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600 }}
              >
                {islemYapiliyor ? 'İşleniyor...' : '⇌ Birleştir ve Sil'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
